import { appConfig, type SupportedLocale } from '@/app/config/app.config';

const SUPPORTED = new Set<SupportedLocale>(appConfig.supportedLocales);

function isSupported(value: string): value is SupportedLocale {
  return SUPPORTED.has(value as SupportedLocale);
}

/**
 * Read the persisted locale override (if any). Returns undefined when not set
 * or running outside a browser.
 */
export function readStoredLocale(): SupportedLocale | undefined {
  if (typeof window === 'undefined' || !window.localStorage) {
    return undefined;
  }
  const stored = window.localStorage.getItem(appConfig.localeStorageKey);
  if (stored && isSupported(stored)) {
    return stored;
  }
  return undefined;
}

/**
 * Persist the locale override. Pass `null` to clear.
 */
export function writeStoredLocale(locale: SupportedLocale | null): void {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }
  if (locale === null) {
    window.localStorage.removeItem(appConfig.localeStorageKey);
  } else {
    window.localStorage.setItem(appConfig.localeStorageKey, locale);
  }
}

/**
 * Detect the initial locale:
 *   1. Stored override
 *   2. navigator.language prefix match against supported locales
 *   3. defaultLocale
 */
export function detectInitialLocale(): SupportedLocale {
  const stored = readStoredLocale();
  if (stored !== undefined) {
    return stored;
  }
  if (typeof navigator !== 'undefined' && typeof navigator.language === 'string') {
    const navLocale = navigator.language.split('-')[0];
    if (navLocale && isSupported(navLocale)) {
      return navLocale;
    }
  }
  return appConfig.defaultLocale;
}
